import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Spin, Button, Modal, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import TransactionCreationForm from "../../Forms/TransactionCreationForm/TransactionCreationForm";

const TransactionTable = ({
  history,
  loading,
  data,
  callReFetch,
  dispatch,
  handleDelete,
  discountData,
  modalForm,
  isAdmin,
  pagination
}) => {

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Detect window size for mobile view
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleOpenEditModal = (id) => {
    const transactionToEdit = data.find(
      (transaction) => transaction._id === id
    );
    if (transactionToEdit) {
      setSelectedTransaction(transactionToEdit);
      setIsEditModalVisible(true);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setSelectedTransaction(null);
  };

  const renderActionButton = (id) => {
    if (!isAdmin) return null;
    
    return (
      <>
        <Tooltip title="Edit">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleOpenEditModal(id)}
            shape="circle"
            style={{ marginRight: 8 }}
          />
        </Tooltip>
        <Tooltip title="Delete">
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(id)}
            shape="circle"
            danger
          />
        </Tooltip>
      </>
    );
  };

  const handleTableOnChange = (pagination, filteredInfo, sorter) => {
    dispatch({ type: "sorter", payload: sorter });
    dispatch({ type: "filteredInfo", payload: filteredInfo });
    callReFetch();
  };

  const columns = [
    {
      title: "Transaction Date",
      dataIndex: "transactionDate",
      key: "transactionDate",
      sorter: true,
      render: (value) => (value ? new Date(value).toLocaleDateString() : "N/A"), // Formats date only
    },

    { title: "No Bon", dataIndex: "noBon", key: "noBon", sorter: true },
    {
      title: "Article",
      dataIndex: "article",
      key: "article",
    },
    { title: "Barcode", dataIndex: "barcode", key: "barcode", sorter: true },
    { title: "Size", dataIndex: "size", key: "size", sorter: true },
    {
      title: "Counter Code",
      dataIndex: "counterCode",
      key: "counterCode",
    },
    {
      title: "Counter Name",
      dataIndex: "counterName",
      key: "counterName",
    },
    {
      title: "Department",
      dataIndex: "counterDept",
      key: "counterDept",
    },
    {
      title: "Brand",
      dataIndex: ["articleDetails", "brand"],
      key: "brand",
      sorter: true,
    },
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      sorter: true,
    },
    {
      title: "Category",
      dataIndex: ["articleDetails", "category"],
      key: "category",
      sorter: true,
    },
    {
      title: "Subcategory",
      dataIndex: ["articleDetails", "subcategory"],
      key: "subcategory",
      sorter: true,
    },
    {
      title: "Division",
      dataIndex: ["articleDetails", "division"],
      key: "division",
      sorter: true,
    },
    {
      title: "Type",
      dataIndex: ["articleDetails", "type"],
      key: "type",
      sorter: true,
    },
    { title: "Quantity", dataIndex: "quantity", key: "quantity", sorter: true },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      sorter: true,
      render: (value) => "Rp " + value?.toLocaleString() || "0", // Prevent undefined error
    },
    {
      title: "discount % ",
      dataIndex: "discount",
      key: "discount",
      sorter: true,
      render: (value) => (value ? value + "%" : ""),
    },
    {
      title: "discountRupiah",
      dataIndex: "discountRupiah",
      key: "discountRupiah",
      sorter: true,
      render: (value) => (value ? "Rp " + value.toLocaleString() : "Rp 0"),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      sorter: true,
      render: (value) => "Rp " + value?.toLocaleString() || "0", // Prevent undefined error
    },
    {
      title: "HPP",
      dataIndex: "hpp",
      key: "hpp",
      sorter: true,
      render: (value) => "Rp " + value?.toLocaleString() || "0", // Prevent undefined error
    },
    {
      title: "Keterangan",
      dataIndex: "keterangan",
      key: "keterangan",
      sorter: true,
    },
    {
      title: "Problematic?",
      dataIndex: "isBermasalah",
      key: "isBermasalah",
      render: (isBermasalah) => (isBermasalah ? "Yes" : "No"),
    },
    {
      title: "Action",
      render: (item) => renderActionButton(item._id),
      width: 200,
    },
  ];

  return (
    <div style={{ width: "80vw" }}>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={data}
          onChange={handleTableOnChange}
          rowKey="_id"
          pagination={pagination || { pageSize: 10 }}
          scroll={{ x: "max-content" }} // Enables horizontal scrolling if needed
        />
      </Spin>
      <Modal
        title="Edit Transaction"
        open={isEditModalVisible}
        onCancel={handleCloseEditModal}
        footer={null}
      >
        {selectedTransaction && (
          <TransactionCreationForm
            transactionData={selectedTransaction}
            onClose={handleCloseEditModal}
            callReFetch={callReFetch}
            discountData={discountData}
            modalForm={modalForm}
            callReFetchTransaction={callReFetch}
            setIsEditModalVisible={setIsEditModalVisible}
            isEditMode
          />
        )}
      </Modal>
    </div>
  );
};

TransactionTable.propTypes = {
  history: PropTypes.object,
  loading: PropTypes.bool,
  data: PropTypes.array,
  callReFetch: PropTypes.func,
  dispatch: PropTypes.func,
  handleDelete: PropTypes.func,
  pagination: PropTypes.object,
  discountData: PropTypes.array,
  modalForm: PropTypes.object,
  isAdmin: PropTypes.bool
};

export default TransactionTable;
